import { useCallback, useEffect, useRef, useState } from 'react'
import {
  AlertTriangle,
  Calendar,
  CheckCircle2,
  Download,
  Eye,
  EyeOff,
  Loader2,
  Mountain,
  RefreshCcw,
  Route,
  Save,
  Target,
  Upload
} from 'lucide-react'

import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Input } from '../ui/input'
import { useToast } from '../ui/toast'
import { useAuth } from '../../hooks/useAuth'
import {
  FocusTypeOption,
  getMilestoneConfig,
  getFocusTypes,
  MilestoneConfigRequest,
  MilestoneConfigResponse,
  MilestoneDefinitionDto,
  MilestoneFocus,
  resetMilestoneConfig,
  updateMilestoneConfig
} from '../../lib/api/milestoneConfig'
import { buildHeaderIndex, escapeCsvValue, makeGetValue, normalizeText, parseBooleanValue, parseCsvText, parseNumberOrNull } from '../../utils/csv'

type MilestoneCsvKey = 'MilestoneNumber' | 'WeekNumber' | 'WeeksFromEnd' | 'Focus' | 'MinPercentage' | 'MaxPercentage' | 'IsPreHikeMilestone' | 'IsHiddenFromUser'

const MILESTONE_CSV_COLUMNS: MilestoneCsvKey[] = [
  'MilestoneNumber', 'WeekNumber', 'WeeksFromEnd', 'Focus', 'MinPercentage', 'MaxPercentage', 'IsPreHikeMilestone', 'IsHiddenFromUser'
]

const MILESTONE_HEADER_ALIASES: Record<MilestoneCsvKey, string[]> = {
  MilestoneNumber: ['milestonenumber', 'milestone', 'number'],
  WeekNumber: ['weeknumber', 'week'],
  WeeksFromEnd: ['weeksfromend', 'fromend', 'weeksbeforeend'],
  Focus: ['focus'],
  MinPercentage: ['minpercentage', 'min', 'minpct'],
  MaxPercentage: ['maxpercentage', 'max', 'maxpct'],
  IsPreHikeMilestone: ['isprehikemilestone', 'prehike', 'prehikemilestone'],
  IsHiddenFromUser: ['ishiddenfromuser', 'hidden', 'hiddenfromuser']
}

const MILESTONE_CSV_FALLBACK: Record<MilestoneCsvKey, number> = {
  MilestoneNumber: 0, WeekNumber: 1, WeeksFromEnd: 2, Focus: 3, MinPercentage: 4, MaxPercentage: 5, IsPreHikeMilestone: 6, IsHiddenFromUser: 7
}

const CONFIGURABLE_WEEK_FOCUS: MilestoneFocus[] = ['Elevation', 'Distance']

const isConfigurableWeekFocus = (
  focus: MilestoneFocus
): focus is Extract<MilestoneFocus, 'Elevation' | 'Distance'> =>
  focus === 'Elevation' || focus === 'Distance'

const focusIcons: Record<MilestoneFocus, React.ReactNode> = {
  Elevation: <Mountain className="h-4 w-4" />,
  Distance: <Route className="h-4 w-4" />,
  UserChoice: <Target className="h-4 w-4" />
}

const focusColors: Record<MilestoneFocus, string> = {
  Elevation: 'bg-orange-100 text-orange-800',
  Distance: 'bg-blue-100 text-blue-800',
  UserChoice: 'bg-purple-100 text-purple-800'
}

export const MilestoneConfigPanel = () => {
  const { user } = useAuth()
  const { addToast } = useToast()
  const [config, setConfig] = useState<MilestoneConfigResponse | null>(null)
  const [focusTypes, setFocusTypes] = useState<FocusTypeOption[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [resetting, setResetting] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  const [isCsvImporting, setIsCsvImporting] = useState(false)
  const [isCsvExporting, setIsCsvExporting] = useState(false)
  const csvInputRef = useRef<HTMLInputElement | null>(null)

  const role = (user?.role ?? 'Guest').toString()
  const isAdmin = role === 'Admin'

  const hydrate = useCallback(async () => {
    setLoading(true)
    try {
      const [configData, focusTypesData] = await Promise.all([
        getMilestoneConfig(),
        getFocusTypes()
      ])
      setConfig(configData)
      setFocusTypes(focusTypesData)
      setHasChanges(false)
    } catch (error) {
      console.error('Failed to load milestone config', error)
      addToast({
        title: 'Could not load config',
        description: 'Please refresh or try again later.'
      })
    } finally {
      setLoading(false)
    }
  }, [addToast])

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false)
      return
    }
    void hydrate()
  }, [hydrate, isAdmin])

  const handleMilestoneChange = (index: number, field: keyof MilestoneDefinitionDto, value: unknown) => {
    if (!config) return

    const updated = [...config.milestones]
    updated[index] = { ...updated[index], [field]: value }
    setConfig({ ...config, milestones: updated })
    setHasChanges(true)
  }

  const handleSettingChange = (field: keyof MilestoneConfigRequest, value: number) => {
    if (!config) return
    setConfig({ ...config, [field]: value })
    setHasChanges(true)
  }

  const handleSave = async () => {
    if (!config) return

    setSaving(true)
    try {
      const request: MilestoneConfigRequest = {
        milestones: config.milestones,
        preHikeMilestoneOffsetDays: config.preHikeMilestoneOffsetDays,
        preHikeMilestonePercentage: config.preHikeMilestonePercentage,
        minimumWeeksForMilestones: config.minimumWeeksForMilestones
      }
      const result = await updateMilestoneConfig(request)
      setConfig(result)
      setHasChanges(false)
      addToast({
        title: 'Config saved',
        description: 'Milestone configuration has been updated.',
        variant: 'success'
      })
    } catch (error) {
      console.error('Failed to save config', error)
      addToast({
        title: 'Save failed',
        description: 'Could not save configuration. Please try again.',
        variant: 'error'
      })
    } finally {
      setSaving(false)
    }
  }

  const handleReset = async () => {
    if (!confirm('Reset milestone configuration to defaults? This will overwrite your current settings.')) {
      return
    }

    setResetting(true)
    try {
      const result = await resetMilestoneConfig()
      setConfig(result)
      setHasChanges(false)
      addToast({
        title: 'Config reset',
        description: 'Milestone configuration has been reset to defaults.',
        variant: 'success'
      })
    } catch (error) {
      console.error('Failed to reset config', error)
      addToast({
        title: 'Reset failed',
        description: 'Could not reset configuration. Please try again.',
        variant: 'error'
      })
    } finally {
      setResetting(false)
    }
  }

  const triggerCsvImport = () => {
    if (csvInputRef.current) {
      csvInputRef.current.value = ''
      csvInputRef.current.click()
    }
  }

  const handleExportCsv = () => {
    if (!config) return
    setIsCsvExporting(true)
    try {
      // Settings row as a comment line
      const settingsLine = `# Settings: MinWeeks=${config.minimumWeeksForMilestones},PreHikeOffsetDays=${config.preHikeMilestoneOffsetDays},PreHikePercentage=${Math.round(config.preHikeMilestonePercentage * 100)}`

      const rows = config.milestones.map((m) => [
        m.milestoneNumber.toString(),
        m.weekNumber != null ? m.weekNumber.toString() : '',
        m.weeksFromEnd != null ? m.weeksFromEnd.toString() : '',
        m.isPreHikeMilestone ? '' : m.focus,
        m.isPreHikeMilestone ? '' : Math.round(m.minPercentage * 100).toString(),
        m.isPreHikeMilestone ? '' : Math.round(m.maxPercentage * 100).toString(),
        m.isPreHikeMilestone.toString(),
        m.isPreHikeMilestone ? '' : m.isHiddenFromUser.toString()
      ])

      const headerLine = MILESTONE_CSV_COLUMNS.map(escapeCsvValue).join(',')
      const dataLines = rows.map((cols) => cols.map(escapeCsvValue).join(','))
      const csv = [settingsLine, headerLine, ...dataLines].join('\n')

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      const timestamp = new Date().toISOString().slice(0, 10)
      link.href = url
      link.download = `hikestrong-milestone-config-${timestamp}.csv`
      link.click()
      URL.revokeObjectURL(url)
      addToast({ title: 'CSV exported', description: `Exported ${rows.length} milestones.` })
    } catch (error) {
      console.error('Failed to export milestone CSV', error)
      addToast({ title: 'Export failed', description: 'Could not export the CSV file.' })
    } finally {
      setIsCsvExporting(false)
    }
  }

  const handleImportCsv = async (file: File) => {
    setIsCsvImporting(true)
    try {
      const text = await file.text()
      // Parse the whole document so quoted multi-line fields round-trip.
      const parsedRows = parseCsvText(text)
      if (parsedRows.length === 0) throw new Error('CSV file is empty.')

      // Parse optional settings comment line
      let minWeeks = config?.minimumWeeksForMilestones ?? 6
      let preHikeOffsetDays = config?.preHikeMilestoneOffsetDays ?? 8
      let preHikePercentage = config?.preHikeMilestonePercentage ?? 0.5

      const rows: string[][] = []
      for (const row of parsedRows) {
        // Comment lines are split on commas like any row; rejoin them to read the settings.
        const line = row.join(',')
        if (line.trim().startsWith('# Settings:')) {
          const settingsStr = line.replace(/^#\s*Settings:\s*/, '')
          for (const pair of settingsStr.split(',')) {
            const [key, val] = pair.split('=')
            const k = normalizeText(key ?? '')
            const v = parseNumberOrNull(val ?? '')
            if (k === 'minweeks' && v !== null) minWeeks = v
            if (k === 'prehikeoffsetdays' && v !== null) preHikeOffsetDays = v
            if (k === 'prehikepercentage' && v !== null) preHikePercentage = v / 100
          }
        } else if (!line.trim().startsWith('#')) {
          rows.push(row)
        }
      }

      if (rows.length === 0) throw new Error('CSV contains no data rows.')

      const header = rows[0].map((v) => v.trim())
      const hasHeader =
        header.some((c) => normalizeText(c) === 'milestonenumber' || normalizeText(c) === 'milestone') &&
        header.some((c) => normalizeText(c) === 'focus')

      const dataRows = hasHeader ? rows.slice(1) : rows
      if (dataRows.length === 0) throw new Error('CSV appears to contain only headers.')

      const headerIndex = hasHeader ? buildHeaderIndex(header, MILESTONE_HEADER_ALIASES) : null
      const getValue = makeGetValue(headerIndex, MILESTONE_CSV_FALLBACK)

      const milestones: MilestoneDefinitionDto[] = dataRows.map((row, idx) => {
        const lineNumber = hasHeader ? idx + 2 : idx + 1
        const milestoneNumber = parseNumberOrNull(getValue(row, 'MilestoneNumber'))
        if (milestoneNumber === null || milestoneNumber < 1) {
          throw new Error(`Row ${lineNumber}: invalid milestone number.`)
        }

        const isPreHikeMilestone = parseBooleanValue(getValue(row, 'IsPreHikeMilestone') || 'false')
        const focusStr = getValue(row, 'Focus')
        const focus = isPreHikeMilestone
          ? 'UserChoice'
          : CONFIGURABLE_WEEK_FOCUS.find((f) => normalizeText(f) === normalizeText(focusStr))

        if (!focus) {
          throw new Error(`Row ${lineNumber}: invalid focus "${focusStr}". Week milestones must be Elevation or Distance.`)
        }

        const minPct = isPreHikeMilestone ? null : parseNumberOrNull(getValue(row, 'MinPercentage'))
        const maxPct = isPreHikeMilestone ? null : parseNumberOrNull(getValue(row, 'MaxPercentage'))

        return {
          milestoneNumber,
          weekNumber: parseNumberOrNull(getValue(row, 'WeekNumber')),
          weeksFromEnd: parseNumberOrNull(getValue(row, 'WeeksFromEnd')),
          focus,
          minPercentage: minPct !== null ? minPct / 100 : preHikePercentage,
          maxPercentage: maxPct !== null ? maxPct / 100 : preHikePercentage,
          isPreHikeMilestone,
          isHiddenFromUser: isPreHikeMilestone
            ? true
            : parseBooleanValue(getValue(row, 'IsHiddenFromUser') || 'false')
        }
      })

      const request: MilestoneConfigRequest = {
        milestones,
        minimumWeeksForMilestones: minWeeks,
        preHikeMilestoneOffsetDays: preHikeOffsetDays,
        preHikeMilestonePercentage: preHikePercentage
      }

      const result = await updateMilestoneConfig(request)
      setConfig(result)
      setHasChanges(false)

      addToast({
        title: 'Import complete',
        description: `Imported ${milestones.length} milestones.`
      })
    } catch (error) {
      console.error('Failed to import milestone CSV', error)
      addToast({
        title: 'Import failed',
        description: error instanceof Error ? error.message : 'Could not parse or import the CSV file.'
      })
    } finally {
      setIsCsvImporting(false)
    }
  }

  if (!isAdmin) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Access Denied
            </CardTitle>
            <CardDescription>
              You need Admin privileges to access this page.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!config) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>No Configuration</CardTitle>
            <CardDescription>
              Could not load milestone configuration.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={hydrate}>Retry</Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  const configurableFocusTypes = focusTypes.filter((ft) => isConfigurableWeekFocus(ft.value))
  const renderNotAppliedCell = (title: string) => (
    <span className="text-muted-foreground" title={title}>-</span>
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Milestone Schedule</h2>
          <p className="text-muted-foreground">
            Configure milestone challenge workouts for training plans
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={csvInputRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0]
              if (file) void handleImportCsv(file)
            }}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={triggerCsvImport}
            disabled={isCsvImporting || loading || saving}
            className="gap-2"
          >
            {isCsvImporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            Import CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCsv}
            disabled={isCsvExporting || loading || !config}
            className="gap-2"
          >
            {isCsvExporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Export CSV
          </Button>
          <Button variant="outline" onClick={handleReset} disabled={resetting}>
            {resetting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCcw className="mr-2 h-4 w-4" />}
            Reset to Defaults
          </Button>
          <Button onClick={handleSave} disabled={saving || !hasChanges}>
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Save Changes
          </Button>
        </div>
      </div>

      {/* Info about last update */}
      {config.updatedAt && (
        <p className="text-sm text-muted-foreground">
          Last updated: {new Date(config.updatedAt).toLocaleString()}
          {config.updatedBy && ` by ${config.updatedBy}`}
        </p>
      )}

      {/* Cross-reference to generation config */}
      <p className="rounded-md bg-muted/50 px-3 py-2 text-xs leading-relaxed text-muted-foreground">
        Note: the milestone minimum week and the elevation-split rules (2/3-workout elevation thresholds and lead
        workout fraction) are versioned algorithm tunables — configure them per config version on the Generation
        Config tab.
      </p>

      {/* General Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            General Settings
          </CardTitle>
          <CardDescription>
            Configure when milestones are applied and the pre-hike milestone timing
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 sm:grid-cols-3">
            <div className="space-y-2">
              <label htmlFor="min-weeks" className="text-sm font-medium">Minimum Weeks for Milestones</label>
              <Input
                id="min-weeks"
                type="number"
                min={1}
                max={20}
                value={config.minimumWeeksForMilestones}
                onChange={(e) => handleSettingChange('minimumWeeksForMilestones', parseInt(e.target.value) || 6)}
              />
              <p className="text-xs text-muted-foreground">
                Plans shorter than this skip milestone generation
              </p>
            </div>
            <div className="space-y-2">
              <label htmlFor="pre-hike-offset" className="text-sm font-medium">Pre-Hike Offset (Days)</label>
              <Input
                id="pre-hike-offset"
                type="number"
                min={1}
                max={30}
                value={config.preHikeMilestoneOffsetDays}
                onChange={(e) => handleSettingChange('preHikeMilestoneOffsetDays', parseInt(e.target.value) || 8)}
              />
              <p className="text-xs text-muted-foreground">
                Days before hike for the final milestone
              </p>
            </div>
            <div className="space-y-2">
              <label htmlFor="pre-hike-pct" className="text-sm font-medium">Pre-Hike Percentage</label>
              <Input
                id="pre-hike-pct"
                type="number"
                min={0}
                max={100}
                step={5}
                value={Math.round(config.preHikeMilestonePercentage * 100)}
                onChange={(e) => handleSettingChange('preHikeMilestonePercentage', (parseInt(e.target.value) || 50) / 100)}
              />
              <p className="text-xs text-muted-foreground">
                % of hardest day for pre-hike milestone
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Milestones Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Milestone Definitions
          </CardTitle>
          <CardDescription>
            Each milestone targets a percentage of the goal hike&apos;s hardest day.
            Fitness level determines where in the min-max range the user falls.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="mb-3 text-xs leading-relaxed text-muted-foreground">
            When &quot;Weeks from End&quot; is set, the milestone is placed at (final week − weeks-from-end), so it
            tracks the end of the plan regardless of plan length; this takes precedence over the absolute week number.
            Milestones never land before the minimum milestone week set on the Generation Config tab.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="px-3 py-2 text-left font-medium">#</th>
                  <th className="px-3 py-2 text-left font-medium">Week</th>
                  <th className="px-3 py-2 text-left font-medium">Weeks from End</th>
                  <th className="px-3 py-2 text-left font-medium">Focus</th>
                  <th className="px-3 py-2 text-left font-medium">Min %</th>
                  <th className="px-3 py-2 text-left font-medium">Max %</th>
                  <th className="px-3 py-2 text-left font-medium">Pre-Hike</th>
                  <th className="px-3 py-2 text-left font-medium">Hidden</th>
                </tr>
              </thead>
              <tbody>
                {config.milestones.map((milestone, idx) => {
                  const isPreHikeMilestone = milestone.isPreHikeMilestone
                  const focusValue = isConfigurableWeekFocus(milestone.focus) ? milestone.focus : ''
                  const focusColor = focusValue ? focusColors[focusValue] : 'bg-muted text-muted-foreground'

                  return (
                  <tr key={idx} className="border-b hover:bg-muted/50">
                    <td className="px-3 py-2">
                      <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground">
                        {milestone.milestoneNumber}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      {isPreHikeMilestone ? (
                        <span className="text-muted-foreground italic">Date-based</span>
                      ) : (
                        <Input
                          type="number"
                          min={1}
                          max={20}
                          className="w-16"
                          value={milestone.weekNumber ?? ''}
                          onChange={(e) => handleMilestoneChange(idx, 'weekNumber', parseInt(e.target.value) || null)}
                        />
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {isPreHikeMilestone ? (
                        renderNotAppliedCell('Pre-hike milestones are scheduled by date, not week')
                      ) : (
                        <Input
                          type="number"
                          min={1}
                          max={20}
                          className="w-16"
                          value={milestone.weeksFromEnd ?? ''}
                          onChange={(e) => handleMilestoneChange(idx, 'weeksFromEnd', parseInt(e.target.value) || null)}
                          title="Milestone week = final week − this value; overrides the absolute week number"
                        />
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {isPreHikeMilestone ? (
                        renderNotAppliedCell('Pre-hike focus is determined by the pre-hike milestone flow')
                      ) : (
                        <select
                          className={`rounded-md px-2 py-1 text-xs font-medium ${focusColor}`}
                          value={focusValue}
                          onChange={(e) => handleMilestoneChange(idx, 'focus', e.target.value as MilestoneFocus)}
                        >
                          {!focusValue && (
                            <option value="" disabled>
                              Select focus
                            </option>
                          )}
                          {configurableFocusTypes.map((ft) => (
                            <option key={ft.value} value={ft.value}>
                              {ft.label}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {isPreHikeMilestone ? (
                        renderNotAppliedCell('Pre-hike load uses the general Pre-Hike Percentage setting')
                      ) : (
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            min={0}
                            max={200}
                            step={5}
                            className="w-16"
                            value={Math.round(milestone.minPercentage * 100)}
                            onChange={(e) => handleMilestoneChange(idx, 'minPercentage', (parseInt(e.target.value) || 0) / 100)}
                          />
                          <span className="text-muted-foreground">%</span>
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {isPreHikeMilestone ? (
                        renderNotAppliedCell('Pre-hike load uses the general Pre-Hike Percentage setting')
                      ) : (
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            min={0}
                            max={200}
                            step={5}
                            className="w-16"
                            value={Math.round(milestone.maxPercentage * 100)}
                            onChange={(e) => handleMilestoneChange(idx, 'maxPercentage', (parseInt(e.target.value) || 0) / 100)}
                          />
                          <span className="text-muted-foreground">%</span>
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() => handleMilestoneChange(idx, 'isPreHikeMilestone', !milestone.isPreHikeMilestone)}
                        className={`rounded-full p-1 ${
                          milestone.isPreHikeMilestone
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-400'
                        }`}
                      >
                        {milestone.isPreHikeMilestone ? (
                          <CheckCircle2 className="h-4 w-4" />
                        ) : (
                          <div className="h-4 w-4" />
                        )}
                      </button>
                    </td>
                    <td className="px-3 py-2">
                      {isPreHikeMilestone ? (
                        renderNotAppliedCell('Pre-hike milestones are always hidden from milestone labels')
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleMilestoneChange(idx, 'isHiddenFromUser', !milestone.isHiddenFromUser)}
                          className={`rounded-full p-1 ${
                            milestone.isHiddenFromUser
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-gray-100 text-gray-400'
                          }`}
                          title={milestone.isHiddenFromUser ? 'Hidden from user' : 'Visible to user'}
                        >
                          {milestone.isHiddenFromUser ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      )}
                    </td>
                  </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Focus Types</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            {configurableFocusTypes.map((ft) => (
              <div key={ft.value} className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium ${focusColors[ft.value]}`}>
                  {focusIcons[ft.value]}
                  {ft.label}
                </span>
                <span className="text-xs text-muted-foreground">{ft.description}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Unsaved changes warning */}
      {hasChanges && (
        <div className="fixed bottom-4 right-4 rounded-lg bg-amber-100 px-4 py-2 text-amber-800 shadow-lg">
          You have unsaved changes
        </div>
      )}
    </div>
  )
}
